import React, { FC, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { MainFrame } from '../components/main-frame';
import { useHttpMutation } from '../../../api';
import { PasswordInput, useAlert } from '../../../shared/components';
import { Button, TextField } from '@mui/material';
import { EMAIL_PATTERN } from '../../../shared/consts';
import { Texts } from '../../../shared/types';
import { RESET_PASSWORD_ROUTES, SIGN_IN_ROUTES } from './route.definitions';
import { useNavigate, useSearchParams } from 'react-router-dom';

export declare type RequestResetPasswordInput = {
  email: string;
};

export const useRequestPasswordReset = () =>
  useHttpMutation<{ token: string }, RequestResetPasswordInput>(
    '/reset-password-link',
    'POST'
  );

export interface RequestResetPasswordProps {
  texts?: Texts<'resetPasswordTitle' | 'button'>;
}

export const RequestResetPassword: FC<RequestResetPasswordProps> = ({
  texts = {
    resetPasswordTitle: RESET_PASSWORD_ROUTES.TITLE,
    button: 'Send reset password link',
  },
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RequestResetPasswordInput>({
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
  });

  const { mutate: requestResetPassword, isLoading } = useRequestPasswordReset();
  const { addAlert } = useAlert();

  const onSubmit: SubmitHandler<RequestResetPasswordInput> = data => {
    requestResetPassword(data, {
      onSuccess: () => {
        addAlert({
          message: 'Reset password link sent',
          type: 'success',
        });
        reset();
      },
      onError: err => {
        addAlert({
          message:
            err?.response?.data?.message ||
            'Failed to send reset password link',
          type: 'error',
        });
      },
    });
  };

  return (
    <MainFrame
      eyebrow="Locked out?"
      title={texts.resetPasswordTitle}
      subtitle="Enter your account email and we’ll send you a link to choose a new password."
      stackProps={{ onSubmit: handleSubmit(onSubmit) }}
      texts={{
        linkMessage: 'Back to sign in',
        forgotPassword: '',
      }}
      linkPath={SIGN_IN_ROUTES.ABSOLUTE_PATH}
    >
      <TextField
        {...register('email', {
          required: `Email is required.`,
          pattern: EMAIL_PATTERN,
        })}
        label="Email"
        type="email"
        autoComplete="email"
        inputMode="email"
        error={!!errors.email}
        helperText={errors.email?.message || undefined}
        fullWidth
      />

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={isLoading}
        fullWidth
      >
        {isLoading ? 'Sending link…' : texts.button}
      </Button>
    </MainFrame>
  );
};

export declare type ResetPasswordInput = {
  password: string;
  confirmPassword: string;
  token: string;
};

export const useResetPassword = () =>
  useHttpMutation<
    { message: string },
    Omit<ResetPasswordInput, 'confirmPassword'>
  >('/reset-password', 'POST');

export const ResetPasswordPage: FC<RequestResetPasswordProps> = ({
  texts = {
    resetPasswordTitle: RESET_PASSWORD_ROUTES.TITLE,
    button: 'Reset Password',
  },
}) => {
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get('token'), [searchParams]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    control,
  } = useForm<Omit<ResetPasswordInput, 'token'>>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const { mutate: resetPassword, isLoading } = useResetPassword();

  const { addAlert } = useAlert();

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<Omit<ResetPasswordInput, 'token'>> = data => {
    if (!token) {
      addAlert({
        message: 'Invalid reset password link',
        type: 'error',
      });

      navigate(SIGN_IN_ROUTES.ABSOLUTE_PATH);
      return;
    }

    resetPassword(
      { ...data, token },
      {
        onSuccess: () => {
          addAlert({
            message: 'Password reset successful',
            type: 'success',
          });
          reset();
          navigate(SIGN_IN_ROUTES.ABSOLUTE_PATH);
        },
        onError: err => {
          addAlert({
            message: err?.response?.data?.message || 'Error resetting password',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <>
      {!token ? (
        <RequestResetPassword />
      ) : (
        <MainFrame
          eyebrow="Almost there"
          title={texts.resetPasswordTitle}
          subtitle="Choose a new password with at least 8 characters."
          stackProps={{ onSubmit: handleSubmit(onSubmit) }}
          texts={{
            linkMessage: 'Back to sign in',
            forgotPassword: '',
          }}
          linkPath={SIGN_IN_ROUTES.ABSOLUTE_PATH}
        >
          <PasswordInput<Omit<ResetPasswordInput, 'token'>>
            controllerProps={{
              control,
              name: 'password',
              rules: {
                required: `Password is required.`,
                validate: value =>
                  value.length >= 8 || `Password is too short.`,
              },
            }}
            textFieldProps={{
              label: 'New password',
              autoComplete: 'new-password',
              error: !!errors.password,
              helperText: errors.password?.message || undefined,
              fullWidth: true,
            }}
          />

          <PasswordInput<Omit<ResetPasswordInput, 'token'>>
            controllerProps={{
              control,
              name: 'confirmPassword',
              rules: {
                required: `Confirm Password is required.`,
                validate: value =>
                  value === getValues('password') || `Passwords do not match.`,
              },
            }}
            textFieldProps={{
              label: 'Confirm new password',
              autoComplete: 'new-password',
              error: !!errors.confirmPassword,
              helperText: errors.confirmPassword?.message || undefined,
              fullWidth: true,
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? 'Saving…' : texts.button}
          </Button>
        </MainFrame>
      )}
    </>
  );
};
